/**
 * The avatar drawn for someone whose picture is switched on but who has no
 * picture: the first letter of their username, in ink, on a colour picked from
 * their uid. It replaced the grey silhouette (owner, 2026-10-23), which made a
 * grid of sellers without photos read as an unfinished marketplace. Drawn by
 * src/components/ui/generated-avatar.tsx; this half is pure (no React, no DOM)
 * so the node test runner can pin it (generated-avatar.test.ts).
 *
 * SEEDED BY THE UID, NOT RANDOM, and nothing is stored. The colour is a hash of
 * the uid, so one person has one colour on every surface (a product card, their
 * reviews, their storefront, a seller's Customers page) and the server and the
 * browser can never disagree about it. The uid rather than the username,
 * because a username can change and the colour should not change with it; the
 * letter does follow the name. Because nothing is written, there is no field to
 * add to the rules' allowlists, no repair branch and no migration: every
 * account without a picture has its avatar the moment this ships.
 *
 * ONLY WHERE A PICTURE WOULD SHOW. This is what the picture slot holds when the
 * picture is on and missing (or fails to load), never a reason to show a slot.
 * `settings.showProfilePicture` is still the one switch: off, the avatar is
 * hidden exactly as before, and nothing here is drawn in its place. A caller
 * with no uid to draw from (a deleted account, a guest, a switched-off picture
 * on a page that always draws a slot) gets null and shows the silhouette.
 *
 * ONE PALETTE FOR BOTH THEMES. A person's colour is theirs, like a photo, so it
 * does not change with the page's light or dark mode, and the ink letter clears
 * WCAG AA on every fill with room to spare at 16px. The hues stay out of the
 * colours that already mean something here: violet (light, never paint), the
 * rating star's gold, and Diamond's cyan.
 */

import { deriveSeed } from './seeded-random';

/** The letter's colour on every fill: zinc 900, the light theme's ink. */
export const AVATAR_INK = '#18181b';

export type AvatarColour = {
  /** A name for the colour, for tests and for anyone reading the list. */
  name: string;
  /** The disc's fill, `#RRGGBB`. */
  fill: string;
};

/**
 * The fills, chosen together and checked on the light card, the light page,
 * the dark card and the dark page.
 *
 * A person's colour is their uid's hash modulo this list's LENGTH, so adding,
 * removing or reordering a colour moves almost everyone to a different one.
 * That is a design change to make on purpose, never a tidy-up; the pinned
 * uids in generated-avatar.test.ts fail until it is acknowledged there.
 */
export const AVATAR_PALETTE: readonly AvatarColour[] = [
  { name: 'coral', fill: '#FF7A5C' },
  { name: 'tangerine', fill: '#FF9F45' },
  { name: 'lime', fill: '#B4E05A' },
  { name: 'mint', fill: '#4FD69C' },
  { name: 'sky', fill: '#5CB8FF' },
  { name: 'cornflower', fill: '#7FA5FF' },
  { name: 'pink', fill: '#FF8CC6' },
];

/** The colour a uid draws in. */
export function avatarColour(seed: string): AvatarColour {
  return AVATAR_PALETTE[deriveSeed(0, `generated-avatar:${seed}`) % AVATAR_PALETTE.length];
}

/**
 * The letter drawn for a name: its first letter or digit, upper-cased, or ''
 * when it has none.
 *
 * Usernames are `[a-zA-Z0-9_]{3,20}` (the updateUsername callable), so in
 * practice this skips a leading underscore and upper-cases one ASCII letter.
 * It still treats anything else sensibly, because some rows label a person
 * with whatever name they have: the text is composed first (a decomposed
 * accent keeps its letter), a letter outside the Basic Multilingual Plane is
 * taken whole, and an upper case that is more than one letter ('ß' to 'SS')
 * keeps the original rather than drawing two. `toUpperCase` and not the
 * locale's, so the server and every browser draw the same letter.
 */
export function avatarInitial(name: unknown): string {
  if (typeof name !== 'string') return '';
  const letter = name.normalize('NFC').match(/[\p{L}\p{N}]/u)?.[0];
  if (!letter) return '';
  const upper = letter.toUpperCase();
  return [...upper].length === 1 ? upper : letter;
}

export type GeneratedAvatar = {
  /** One letter or digit; '' draws a figure instead (a name with none). */
  initial: string;
  /** The disc's fill. */
  fill: string;
  /** The letter's (or the figure's) colour. */
  ink: string;
};

/**
 * What to draw for this person, or null when there is no uid to draw from
 * (the caller shows the plain silhouette). Both arguments are typed unknown
 * because they come off client-written, world-readable documents.
 */
export function generatedAvatarFor(seed: unknown, name: unknown): GeneratedAvatar | null {
  if (typeof seed !== 'string' || seed.trim() === '') return null;
  return { initial: avatarInitial(name), fill: avatarColour(seed).fill, ink: AVATAR_INK };
}
